import { Box, Button, Typography, useMediaQuery, Theme } from '@mui/material';
import Image from 'next/image';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperClass } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';
import { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/router';

const images = [
  {
    src: '/images/Walkthrough1.png',
    title: 'Easily create a shared story using one of our guided templates.',
    author: 'Josh Miller',
  },
  {
    src: '/images/Walkthrough2.png',
    title: 'Collaborate with others to add memories, shared experiences, and stories.',
    author: 'Emma Watson',
  },
  { src: '/images/Walkthrough3.png', title: 'Relive your shared memories, anytime, anywhere!', author: 'John Doe' },
];

const getHighlightedText = (text: string) => {
  const wordsToHighlight = ['create', 'collaborate', 'relive'];

  return text.split(' ').map((word, index) => {
    if (wordsToHighlight.includes(word.toLowerCase())) {
      return (
        <span key={index} style={{ color: '#0072CE', fontWeight: '400' }}>
          {word}{' '}
        </span>
      );
    }
    return word + ' ';
  });
};

export default function WalkThrough({ handleWalkthroughComplete }: any) {
  const swiperRef = useRef<SwiperClass | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const router = useRouter();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const useBodyBackground = (color: string = '#fff') => {
    useEffect(() => {
      document.body.style.background = color;
      return () => {
        document.body.style.background = '';
      };
    }, [color]);
  };

  useBodyBackground('linear-gradient(108.71deg, #D6E7F7 8.5%, #BDE0FF 93.82%)');

  const handleNextClick = () => {
    if (currentIndex === images.length - 1) {
      handleWalkthroughComplete();
    } else {
      swiperRef.current?.slideNext();
    }
  };

  return (
    <Box mt={'2.5rem'} height={'90vh'} sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* Center Content */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          textAlign: 'center',
        }}>
        {/* Image Slider */}
        <Box sx={{ position: 'relative', width: { xs: '90%', md: '50%' }, borderRadius: '10px', overflow: 'hidden' }}>
          <Swiper
            onSwiper={(swiper) => (swiperRef.current = swiper)}
            onSlideChange={(swiper) => setCurrentIndex(swiper.realIndex)}
            slidesPerView={1}
            spaceBetween={10}
            loop={true}
            style={{ borderRadius: '10px' }}>
            {images.map((image, index) => (
              <SwiperSlide key={index}>
                {/* Image Container with Fixed Height */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '400px',
                    width: '350px',
                  }}>
                  <Image priority src={image.src} alt={image.title} fill style={{ objectFit: 'contain' }} />
                </Box>
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>

        {/* Image Caption Below - Ensure It's Outside the Image Container */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 2 }}>
          <Typography
            variant='body1'
            sx={{
              fontSize: '20px',
              width: '100%',
              maxWidth: '400px',
              color: '#333',
              fontWeight: 400,
              textAlign: 'center',
            }}>
            {getHighlightedText(images[currentIndex].title)}
          </Typography>
        </Box>

        {/* Navigation Dots */}
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          {images.map((_, index) => (
            <Box
              key={index}
              sx={{
                width: index === currentIndex ? '31px' : '8px',
                height: '8px',
                bgcolor: index === currentIndex ? '#0072CE' : '#77BCE5',
                borderRadius: '28px',
                transition: '0.3s',
              }}
            />
          ))}
        </Box>

        {/* Next Button */}
        <Button
          variant='contained'
          sx={{
            mt: 2,
            width: '350px',
            height: '58px',
            bgcolor: '#1E2A5E',
            color: 'white',
            px: 4,
            py: 1,
            fontWeight: 'bold',
            borderRadius: '10px',
            ':hover': { bgcolor: '#16204A' },
          }}
          onClick={handleNextClick}>
          {currentIndex === images.length - 1 ? 'Get Started' : 'Next'}
        </Button>
      </Box>
    </Box>
  );
}
